import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// Typen — passen 1:1 zu Backend src/modules/portal-listings/service.ts
// (listListingsForGrundartikel). Bewusst camelCase: Drizzle-Select-Projektion,
// kein snake_case-SQL-Response, kein Case-Mapping im Backend.
export interface PortalListing {
  id: number;
  portal: string;
  accountKey: string;
  url: string;
  title: string | null;
  price: string | null;
  currency: string | null;
  shippingFree: boolean | null;
  shippingCost: string | null;
  status: "active" | "inactive" | "excluded";
  stueckzahl: number;
  soldItemSku: string;
  lastSeenActiveAt: string | null;
  inactiveSince: string | null;
}

export function usePortalListings(sku: string) {
  return useQuery({
    queryKey: ["portal-listings", sku],
    queryFn: async () => {
      const res = await api.get<{ listings: PortalListing[]; total: number }>(
        `/portal-listings/?stock_sku=${encodeURIComponent(sku)}`,
      );
      return res.listings;
    },
    enabled: Boolean(sku),
  });
}

export function useExcludeListing(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/portal-listings/${id}/exclude/`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["portal-listings", sku] }),
  });
}

export function useIncludeListing(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/portal-listings/${id}/include/`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["portal-listings", sku] }),
  });
}

// ---------- Bulk-Abruf fuer die Preis-Kalkulation ----------

/** Schlanke Sicht auf ein Portal-Angebot — nur was die Kalkulations-Badges brauchen. */
export interface PortalListingLite {
  id: number;
  portal: string;
  accountKey: string;
  url: string;
  status: "active" | "inactive" | "excluded";
  inactiveSince: string | null;
}

/** Muss mit BULK_MAX_IDS im Backend (src/modules/portal-listings/bulk-ids.ts) uebereinstimmen. */
export const BULK_MAX_IDS = 200;

/** Teilt eine Liste in Bloecke fester Groesse. Rein. */
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Ergebnis des Bulk-Abrufs. `partialFailure` trennt „fuer diesen Artikel gibt es
 * keinen Eintrag" von „fuer diesen Artikel wurde nie nachgesehen" — ohne dieses
 * Signal saehen beide Faelle in der Oberflaeche identisch aus und der Tooltip
 * wuerde im Fehlerfall etwas behaupten, das nie geprueft wurde.
 */
export interface BulkListingsResult {
  byItem: Record<number, PortalListingLite[]>;
  /** Mindestens ein Block schlug fehl — die uebrigen Bloecke sind trotzdem gueltig. */
  partialFailure: boolean;
}

/**
 * Angebote zu mehreren Artikeln. Stueckelt automatisch an der Server-Grenze und
 * fuehrt die Bloecke zusammen.
 *
 * Die Bloecke laufen ueber Promise.allSettled, damit ein fehlgeschlagener Block
 * die bereits geladenen nicht mitreisst (bei 450 Zeilen sonst: 400 vorliegende
 * Eintraege verworfen, alle Zeilen „keine Daten"). Schlagen ALLE Bloecke fehl,
 * gibt es kein Teilergebnis zu retten — dann wirft die Funktion regulaer, damit
 * die Query in isError laeuft und wiederholt.
 */
export async function fetchListingsByStockItemIds(
  ids: number[],
): Promise<BulkListingsResult> {
  if (ids.length === 0) return { byItem: {}, partialFailure: false };

  const blocks = chunk(ids, BULK_MAX_IDS);
  const settled = await Promise.allSettled(
    blocks.map((block) =>
      api.get<{ listings_by_item: Record<string, PortalListingLite[]> }>(
        `/portal-listings/bulk/?stock_item_ids=${block.join(",")}`,
      ),
    ),
  );

  const byItem: Record<number, PortalListingLite[]> = {};
  let failed = 0;
  for (const r of settled) {
    if (r.status === "rejected") {
      failed += 1;
      continue;
    }
    for (const [key, listings] of Object.entries(r.value.listings_by_item ?? {})) {
      byItem[Number(key)] = listings;
    }
  }

  if (failed === settled.length) {
    const first = settled.find((r) => r.status === "rejected");
    throw (first as PromiseRejectedResult).reason;
  }

  return { byItem, partialFailure: failed > 0 };
}
